import type { Graphics } from 'pixi.js';
import { lerpColor } from '../../color';
import type { Body, Palette, WorldDress, WorldFx } from '../../world';

const TAU = Math.PI * 2;
const TAMBORIM_LUGS = 6;
const SURDO_LUGS = 8;
const TARGET_MARKS = 16;
const PLUMES = 5;

function tonesOf(pal: Palette): readonly number[] {
  return [pal.held, pal.spot, pal.incoming.circle];
}

function curl(g: Graphics, x: number, y: number, radius: number, turns: number, rot: number): void {
  const steps = Math.max(8, Math.round(turns * 10));
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const a = rot + t * turns * TAU;
    const rr = radius * (0.2 + 0.8 * t);
    const px = x + Math.cos(a) * rr;
    const py = y + Math.sin(a) * rr;
    if (i === 0) g.moveTo(px, py);
    else g.lineTo(px, py);
  }
}

function ribbon(g: Graphics, x0: number, y0: number, x1: number, y1: number, amp: number, gap: number): void {
  const len = Math.hypot(x1 - x0, y1 - y0);
  if (len < 1) return;
  const ux = (x1 - x0) / len;
  const uy = (y1 - y0) / len;
  const loops = Math.max(1, Math.ceil(len / gap));
  const steps = loops * 10;
  g.moveTo(x0, y0);
  for (let j = 1; j <= steps; j++) {
    const t = j / steps;
    const th = t * loops * TAU;
    const along = len * t - amp * Math.sin(th);
    const side = amp * 0.7 * (1 - Math.cos(th));
    g.lineTo(x0 + ux * along - uy * side, y0 + uy * along + ux * side);
  }
}

function onRing(body: Body, radius: number, a: number): { x: number; y: number } {
  return { x: body.x + Math.cos(a) * radius * body.scaleX, y: body.y + Math.sin(a) * radius * body.scaleY };
}

export const dress: WorldDress = {
  incomingCircle(k, body, color, alpha) {
    const { g, pal } = k;
    const r = body.radius;
    g.ellipse(body.x, body.y, r * 0.76 * body.scaleX, r * 0.76 * body.scaleY).fill({ color: lerpColor(color, pal.rim, 0.45), alpha: 0.38 * alpha });
    g.ellipse(body.x, body.y, r * 0.87 * body.scaleX, r * 0.87 * body.scaleY);
    g.stroke({ width: Math.max(1.2, r * 0.09), color: lerpColor(color, pal.ink, 0.55), alpha: 0.6 * alpha });
    const lug = Math.max(1, r * 0.06);
    for (let i = 0; i < TAMBORIM_LUGS; i++) {
      const at = onRing(body, r * 0.87, (i / TAMBORIM_LUGS) * TAU + 0.5);
      g.circle(at.x, at.y, lug);
    }
    g.fill({ color: lerpColor(color, pal.rim, 0.6), alpha: 0.75 * alpha });
    k.highlight(body, 0.55 * alpha);
  },

  mineCircle(k, body, alpha, width, fill) {
    const { g, pal } = k;
    const r = body.radius;
    k.trace(body);
    g.fill({ color: lerpColor(fill, pal.heldLight, 0.25), alpha });
    g.ellipse(body.x, body.y, r * 0.36 * body.scaleX, r * 0.36 * body.scaleY).fill({ color: lerpColor(fill, pal.heldShade, 0.55), alpha: 0.4 * alpha });
    g.ellipse(body.x, body.y, r * 0.62 * body.scaleX, r * 0.62 * body.scaleY);
    g.stroke({ width: Math.max(1, r * 0.025), color: pal.heldShade, alpha: 0.35 * alpha });
    g.ellipse(body.x, body.y, r * 0.9 * body.scaleX, r * 0.9 * body.scaleY);
    g.stroke({ width: r * 0.16, color: lerpColor(pal.ink, fill, 0.2), alpha });
    const lug = Math.max(1.2, r * 0.055);
    for (let i = 0; i < SURDO_LUGS; i++) {
      const at = onRing(body, r * 0.9, (i / SURDO_LUGS) * TAU + 0.2);
      g.circle(at.x, at.y, lug);
    }
    g.fill({ color: pal.rim, alpha: 0.9 * alpha });
    k.highlight(body, 0.45 * alpha);
    k.trace(body);
    g.stroke({ width, color: k.outline, alpha, join: 'round' });
  },

  target(k, body, alpha, _spread, reach, spin, edge) {
    const { g, pal } = k;
    const tones = tonesOf(pal);
    k.trace(body);
    g.fill({ color: pal.spot, alpha: 0.1 * alpha });
    const dot = Math.max(1.8, k.unit * 0.01);
    const coil = Math.max(3, k.unit * 0.016);
    k.ringDots(body, reach, TARGET_MARKS, spin, (x, y, i, a) => {
      if (i % 2 === 0) {
        g.circle(x, y, dot).fill({ color: tones[(i / 2) % tones.length] ?? pal.held, alpha: 0.85 * alpha });
        return;
      }
      curl(g, x, y, coil, 1.6, a + spin);
    });
    g.stroke({ width: Math.max(1, k.unit * 0.0045), color: pal.spot, alpha: 0.75 * alpha, cap: 'round', join: 'round' });
    k.trace(body);
    g.stroke({ width: edge, color: pal.rim, alpha: 0.8 * alpha, join: 'round' });
  },

  pip(k, x, y, radius, on, last, alpha, width) {
    const { g, pal } = k;
    const tone = last ? pal.spot : pal.held;
    if (on) g.circle(x, y, radius * 1.9).fill({ color: tone, alpha: 0.2 * alpha });
    g.circle(x, y, radius).fill({ color: on ? lerpColor(tone, pal.rim, 0.15) : lerpColor(pal.bg, pal.rim, 0.16), alpha });
    if (on) g.circle(x - radius * 0.32, y - radius * 0.32, radius * 0.26).fill({ color: pal.rim, alpha: 0.6 * alpha });
    g.circle(x, y, radius).stroke({ width: width * 0.8, color: pal.rim, alpha: (on ? 0.9 : 0.45) * alpha });
  },

  path(k, points, count, alpha) {
    const { g, pal } = k;
    const amp = Math.max(2, k.unit * 0.012);
    for (let i = 2; i < count; i += 2) ribbon(g, points[i - 2]!, points[i - 1]!, points[i]!, points[i + 1]!, amp, amp * 5);
    g.stroke({ width: 1.5, color: pal.path, alpha: alpha * 2.2, cap: 'round', join: 'round' });
  },
};

export const fx: WorldFx = {
  bit(g, x, y, size, i, color, alpha, rot) {
    if (i % 4 === 0) {
      curl(g, x, y, size * 1.1, 1.2, rot);
      g.stroke({ width: Math.max(1, size * 0.4), color, alpha, cap: 'round', join: 'round' });
      return;
    }
    if (i % 4 === 1) g.regularPoly(x, y, size * 0.85, 4, rot);
    else g.circle(x, y, size * 0.75);
    g.fill({ color, alpha });
  },

  milestone(g, m, pal) {
    const tones = tonesOf(pal);
    const open = 0.3 + 0.7 * m.grow;
    for (let p = 0; p < PLUMES; p++) {
      const a = -Math.PI / 2 + (p - (PLUMES - 1) / 2) * 0.42 * open;
      const base = m.r * 0.92;
      const tip = m.r * (0.95 + 0.7 * m.grow);
      const half = m.unit * 0.022 * (0.6 + 0.4 * m.grow);
      const ux = Math.cos(a);
      const uy = Math.sin(a);
      const mid = (base + tip) / 2;
      g.moveTo(m.x + ux * base, m.y + uy * base)
        .quadraticCurveTo(m.x + ux * mid - uy * half * 2, m.y + uy * mid + ux * half * 2, m.x + ux * tip, m.y + uy * tip)
        .quadraticCurveTo(m.x + ux * mid + uy * half * 2, m.y + uy * mid - ux * half * 2, m.x + ux * base, m.y + uy * base)
        .fill({ color: tones[p % tones.length] ?? pal.held, alpha: m.fade * 0.35 });
    }
    for (let s = 0; s < 2; s++) {
      const a = s === 0 ? -0.35 : Math.PI + 0.35;
      const x0 = m.x + Math.cos(a) * m.r;
      const y0 = m.y + Math.sin(a) * m.r;
      const len = m.r * 0.85 * (0.25 + 0.75 * m.grow);
      const amp = m.unit * 0.016 * (1 - 0.45 * m.grow);
      ribbon(g, x0, y0, x0 + Math.cos(a + 0.5) * len, y0 + Math.sin(a + 0.5) * len, amp, amp * 4);
      g.stroke({ width: m.width, color: s === 0 ? pal.held : pal.spot, alpha: m.fade * 0.8, cap: 'round', join: 'round' });
    }
  },

  trail(g, x, y, size) {
    g.circle(x, y, size * 0.85);
  },
};
